// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * ai.ts — the bots. They fill empty seats in a live room, they are the opponent
 * in solo Skirmish, and — most importantly — they are what `balance.test.ts`
 * measures the game with.
 *
 * That last job sets the bar. A bot that cannot maze is not a weak opponent, it
 * is a broken instrument: every balance number the sim prints would be a
 * statement about the bot rather than about the game. Principle "objective must
 * be bot-completable" in its Sporeline form is `balance.test.ts`'s
 * `botsReachLateWaves` assertion — if the bots all die on wave 6, the sim is
 * measuring nothing and no tuning done on top of it means anything.
 *
 * So the build heuristic is a real one: score every legal cell by how much it
 * LENGTHENS THE ROAD and how much road the tower would then cover, and take the
 * best. That produces recognisable serpentine mazes rather than a scatter of
 * towers, which is what a competent human does.
 *
 * ── The three policies exist to be compared, not to be flavour ──────────────
 *
 * `turtle` never sends and pours everything into the maze. `pressure` sends the
 * moment it can afford to and defends on what is left. `balanced` sends only
 * from surplus. If one of these dominates the other two, the send economy is
 * mis-tuned and the game has one correct line — which is a design failure, not a
 * nuance. balance.test.ts asserts all three stay viable against each other.
 */

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import { colOf, roadCells, rowOf } from './field';
import { Game, sendCost, TOWERS, type TowerKind } from './game';

export type Policy = 'turtle' | 'balanced' | 'pressure';

export const POLICIES: readonly Policy[] = ['turtle', 'balanced', 'pressure'];

export interface BotConfig {
  policy: Policy;
  /** Seeded so a match replays identically. */
  seed: number | string;
}

interface Tuning {
  /** Gold held back from sending, as a multiple of the cheapest tower. */
  reserve: number;
  /** Stop sending past this many spores — nobody has infinite gold to burn. */
  sendCap: number;
  /** Prefer upgrading over a new tower once this many towers are down. */
  upgradeAfter: number;
}

/**
 * `balanced`'s reserve was 3.5 (i.e. it held back 49 gold before considering a
 * spore) and the sim caught that this made it a strict synonym for `turtle`:
 * two-player Skirmish came out BYTE-IDENTICAL across seven different send
 * economies, because with the tightened income neither seat ever sent a single
 * spore in any of them. A policy comparison in which two of the three policies
 * are the same policy is not a comparison, and it had been silently true for
 * every measurement taken up to that point.
 */
const TUNING: Record<Policy, Tuning> = {
  turtle: { reserve: Infinity, sendCap: 0, upgradeAfter: 8 },
  balanced: { reserve: 2, sendCap: 14, upgradeAfter: 9 },
  pressure: { reserve: 0.8, sendCap: 22, upgradeAfter: 12 },
};

export class Bot {
  readonly policy: Policy;
  private rng: Rng;

  constructor(config: BotConfig) {
    this.policy = config.policy;
    this.rng = makeRng(config.seed);
  }

  /**
   * One decision pass. Called a few times per wave, not per frame — a bot that
   * re-plans every tick would out-react any human and make the sim optimistic.
   *
   * Returns how many spores it wants to send this pass; the caller routes them,
   * because choosing a victim needs the whole match and a Game only knows itself.
   */
  act(g: Game): number {
    if (g.over) return 0;
    const tune = TUNING[this.policy];

    let sends = 0;
    if (tune.sendCap > 0 && g.stats.sent < tune.sendCap) {
      const floor = TOWERS.spitter.cost * tune.reserve;
      // Only ever one send per pass: a bot that drains its whole purse into
      // spores in a single tick is not a strategy any human can execute, and it
      // would make `pressure` look far stronger than it is.
      if (g.gold - sendCost(g.stats.sent) >= floor) {
        if (g.spendSend() > 0) sends = 1;
      }
    }

    this.build(g, tune);
    return sends;
  }

  /** Build or upgrade, once, if it can afford anything worth having. */
  private build(g: Game, tune: Tuning): void {
    const wave = Math.max(1, g.wave);

    // Late in a run, a third level on a tower covering the long corridor beats a
    // fourth tower covering nothing.
    const preferUpgrade = g.towers.size >= tune.upgradeAfter && this.rng() < 0.55;
    if (preferUpgrade && this.upgradeBest(g)) return;

    const kind = this.pickKind(g, wave);
    if (g.gold < TOWERS[kind].cost) {
      this.upgradeBest(g);
      return;
    }
    const cell = this.bestCell(g, kind);
    if (cell < 0) {
      this.upgradeBest(g);
      return;
    }
    g.place(cell, kind);
  }

  private pickKind(g: Game, wave: number): TowerKind {
    const mires = [...g.towers.values()].filter((t) => t.kind === 'mire').length;
    const lances = [...g.towers.values()].filter((t) => t.kind === 'lance').length;
    // One Mire early pays for itself by multiplying every gun on the corridor;
    // a second is worth it once the corridor is long. Lances arrive when husks
    // do, because nothing else dents them.
    if (wave >= 4 && mires < 1 + Math.floor(g.towers.size / 9) && g.gold >= TOWERS.mire.cost * 1.4) {
      return 'mire';
    }
    if (wave >= 6 && lances < 1 + Math.floor(g.towers.size / 6) && g.gold >= TOWERS.lance.cost * 1.3) {
      return 'lance';
    }
    return 'spitter';
  }

  /**
   * Score every legal cell and take the best.
   *
   * Two terms, and the weighting between them IS the bot's skill: `roadGain` is
   * how many cells longer the road becomes (mazing), `coverage` is how much of
   * the resulting road this tower would actually reach (shooting). Weighting
   * road above coverage is deliberate — a tower that adds eight cells of
   * corridor makes every OTHER tower better, which is the insight the game is
   * built to teach.
   */
  private bestCell(g: Game, kind: TowerKind): number {
    const spec = g.spec;
    const range = TOWERS[kind].range[0];
    const before = g.roadLength();

    let bestCell = -1;
    let bestScore = -Infinity;

    for (const cell of g.candidateCells()) {
      // One breadth-first pass gives BOTH terms: how long the road becomes, and
      // which cells it runs through so coverage can be measured against the
      // road this placement would CREATE rather than the one it is about to
      // move. Rating a tower by the corridor it is displacing is the obvious
      // way to write this and it is wrong.
      const probe = g.evaluate(cell, kind);
      if (!probe.ok) continue;
      const road = probe.road;
      const roadGain = road.length - before;

      const tx = colOf(spec, cell);
      const ty = rowOf(spec, cell);
      let coverage = 0;
      for (const r of road) {
        if (Math.hypot(colOf(spec, r) - tx, rowOf(spec, r) - ty) <= range) coverage++;
      }

      // A trace of noise breaks ties without changing the ranking, so two bots
      // on the same seed do not build byte-identical mazes and the sim samples
      // a slightly wider space.
      const score = roadGain * 3.2 + coverage * 1.6 + this.rng() * 0.4;
      if (score > bestScore) {
        bestScore = score;
        bestCell = cell;
      }
    }
    return bestCell;
  }

  /** Upgrade whichever tower covers the most road. */
  private upgradeBest(g: Game): boolean {
    const spec = g.spec;
    const road = roadCells(spec, g.dist);
    let bestCell = -1;
    let bestCover = -1;

    for (const tower of g.towers.values()) {
      if (tower.level >= 2) continue;
      if (g.gold < TOWERS[tower.kind].upgrade[tower.level]) continue;
      const range = TOWERS[tower.kind].range[tower.level];
      const tx = colOf(spec, tower.cell);
      const ty = rowOf(spec, tower.cell);
      let cover = 0;
      for (const r of road) {
        if (Math.hypot(colOf(spec, r) - tx, rowOf(spec, r) - ty) <= range) cover++;
      }
      // Damage dealers first: upgrading a Mire that covers everything is worth
      // less than upgrading the Lance standing next to it.
      if (TOWERS[tower.kind].damage[tower.level] === 0) cover *= 0.6;
      if (cover > bestCover) {
        bestCover = cover;
        bestCell = tower.cell;
      }
    }
    return bestCell >= 0 ? g.upgrade(bestCell) : false;
  }

  /**
   * Who to send at. The healthiest live rival — kicking the leader is both the
   * obvious human instinct and the only choice that does not hand the game to
   * whoever happens to be ahead.
   *
   * TIES ARE BROKEN AT RANDOM, and that is not a detail. The first version took
   * the lowest index on a tie, which sounds harmless and was not: at the start
   * of a match every Heart is full, so EVERY bot aimed its first spores at seat
   * 0. Seat 0 took the opening beating, collected catch-up income for it, and
   * was then left alone while the tie moved to seat 1 — who got the same
   * treatment with less compensation, and so on down the table. It showed up as
   * Siege 4P seat win rates of 38/24/21/17 over 100 matches: an ordering bias
   * produced entirely by a tie-break, invisible to every other test and to any
   * amount of playing the game.
   */
  pickTarget(hearts: number[], self: number): number {
    let bestHeart = -1;
    const tied: number[] = [];
    for (let i = 0; i < hearts.length; i++) {
      if (i === self || hearts[i] <= 0) continue;
      if (hearts[i] > bestHeart) {
        bestHeart = hearts[i];
        tied.length = 0;
        tied.push(i);
      } else if (hearts[i] === bestHeart) {
        tied.push(i);
      }
    }
    if (!tied.length) return -1;
    return tied[Math.floor(this.rng() * tied.length) % tied.length];
  }
}
